import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Loader2, Lock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, get, errMsg } from "@/lib/api";
import { Panel, StatusBadge } from "@/admin/ui";
import { RichEditor } from "@/admin/RichEditor";
import { PageSeoPanel, PageSettingsPanel, SystemTextsPanel, TokensPanel } from "@/admin/PagePanels";
import { useRefresh } from "@/admin/hooks";

const EMPTY = { title: "", nav_label: "", kicker: "", lead: "", body: "", slug: "", footer_group: "main", in_topbar: false, show_updated: false, order: 20,
  status: "published", seo_title: "", seo_description: "", noindex: false, share_image: null, texts: {} };

const AutoTextarea = ({ className, ...props }) => {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) { ref.current.style.height = "auto"; ref.current.style.height = ref.current.scrollHeight + "px"; } }, [props.value]);
  return <textarea ref={ref} rows={1} className={`w-full resize-none bg-transparent outline-none placeholder:text-muted-foreground/50 ${className}`} {...props} />;
};

const ContentFields = ({ form, patch }) => (
  <>
    <div className="bg-card border border-border px-6 py-5 space-y-3">
      <input value={form.kicker} onChange={(e) => patch({ kicker: e.target.value })} placeholder="Antetítulo (opcional)" className="w-full bg-transparent outline-none kicker text-primary placeholder:text-muted-foreground/50" data-testid="page-kicker-input" />
      <AutoTextarea value={form.title} onChange={(e) => patch({ title: e.target.value })} placeholder="Título de la página" className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tight" data-testid="page-title-input" />
      <AutoTextarea value={form.lead} onChange={(e) => patch({ lead: e.target.value })} placeholder="Entradilla: una o dos frases que resumen la página" className="font-serif text-xl text-muted-foreground leading-relaxed" data-testid="page-lead-input" />
    </div>
    <p className="text-xs text-muted-foreground -mt-3">Consejo: cada «Subtítulo» del texto se muestra como un bloque con el título a la izquierda, como en la web.</p>
    <RichEditor value={form.body} onChange={(body) => patch({ body })} />
  </>
);

export default function PageEditor() {
  const { id } = useParams();
  const isNew = id === "nueva";
  const navigate = useNavigate();
  const refresh = useRefresh();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-page", id], queryFn: () => get(`/admin/pages/${id}`), enabled: !isNew });
  const [form, setForm] = useState(EMPTY);
  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  useEffect(() => { if (isNew) setForm(EMPTY); else if (data) setForm({ ...EMPTY, ...data, texts: data.texts || {} }); }, [data, isNew]);
  const system = data?.kind === "system";

  const save = useMutation({
    mutationFn: () => (isNew ? api.post("/admin/pages", form) : api.put(`/admin/pages/${id}`, form)).then((r) => r.data),
    onSuccess: (doc) => {
      toast.success(isNew ? "Página creada" : "Cambios guardados");
      qc.setQueryData(["admin-page", doc.id], doc);
      refresh();
      if (isNew) navigate(`/admin/paginas/${doc.id}`, { replace: true });
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const trash = useMutation({
    mutationFn: () => api.delete(`/admin/pages/${id}`),
    onSuccess: () => { toast.success("Página movida a la papelera"); refresh(); navigate("/admin/paginas"); },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!isNew && isLoading) return <div className="py-20 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const path = isNew ? `/${form.slug || "…"}` : data.path;
  const live = !isNew && !data.deleted_at && (system ? path.startsWith("/") && !path.includes("…") : data.status === "published");

  return (
    <div data-testid="page-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Link to="/admin/paginas" className="h-9 w-9 grid place-items-center border border-border bg-card hover:border-foreground" aria-label="Volver" data-testid="page-editor-back-link"><ArrowLeft className="h-4 w-4" /></Link>
          <div>
            <p className="kicker text-primary text-[10px]">{isNew ? "Nueva página" : system ? "Página fija" : "Editar página"} · <span className="font-mono normal-case">{path}</span></p>
            <div className="flex items-center gap-2 mt-0.5"><h1 className="font-display font-bold text-xl" data-testid="page-editor-title">{isNew ? "Crear página" : system ? data.title : form.nav_label || form.title}</h1>{!isNew && !system && <StatusBadge status={data.status} testid="page-editor-status" />}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {live && <Button variant="ghost" asChild><a href={path} target="_blank" rel="noopener noreferrer" data-testid="page-view-live-btn"><ExternalLink /> Ver en la web</a></Button>}
          {!isNew && data.deletable && !data.deleted_at && <Button variant="outline" className="bg-card hover:text-destructive" onClick={() => trash.mutate()} disabled={trash.isPending} data-testid="page-trash-btn"><Trash2 /> Eliminar</Button>}
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="page-save-btn">{save.isPending && <Loader2 className="animate-spin" />}{isNew ? "Crear página" : "Guardar cambios"}</Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-8 space-y-6">
          {system ? (
            <>
              <Panel testid="page-system-info"><p className="text-sm text-muted-foreground flex items-start gap-2">{!data.deletable && <Lock className="h-4 w-4 mt-0.5 shrink-0" />}{data.description}</p></Panel>
              <SystemTextsPanel fields={data.fields} texts={form.texts} onChange={(texts) => patch({ texts })} />
            </>
          ) : <ContentFields form={form} patch={patch} />}
        </div>
        <div className="col-span-12 xl:col-span-4 space-y-6">
          {!system && <PageSettingsPanel form={form} patch={patch} />}
          {(!system || data.has_seo) && <PageSeoPanel form={form} patch={patch} path={path} content={!system} />}
          {!system && <TokensPanel />}
        </div>
      </div>
    </div>
  );
}
